import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Card, Btn, Field, Input, Select } from "@/components/UI";
import { toast } from "sonner";

const empty = { company_name: "SLKC", tagline: "Selvalakshmi Knits & Co.", address: "", gst_no: "", phone: "", email: "", default_compactor_id: "", default_compactor_name: "" };

export default function CompanySettingsPage() {
  const [f, setF] = useState(empty);
  const [compactors, setCompactors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setF({ ...empty, ...r.data })).catch(() => {});
    api.get("/parties").then((r) => setCompactors(r.data.filter((p) => p.type === "compactor"))).catch(() => {});
    api.get("/admin/migrate/batches-to-dcs/preview").then((r) => setPreview(r.data)).catch(() => {});
  }, []);

  const runMigration = async () => {
    if (!window.confirm("Convert all old single-line KDCP batches into DCs?\n\nEach batch becomes a one-line DC (MIG-<id>). Ledger entries are transferred to the new DC references so party balances stay identical. Old batch rows are deleted. This action is safe to re-run (already-migrated batches are skipped).")) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/migrate/batches-to-dcs");
      toast.success(`Migrated ${data.migrated} batches · ${data.ledger_transferred} ledger entries transferred`);
      const p = await api.get("/admin/migrate/batches-to-dcs/preview");
      setPreview(p.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setMigrating(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Attach the compactor's name for pipeline UI convenience
      const compactor = compactors.find((c) => c.id === f.default_compactor_id);
      const payload = { ...f, default_compactor_name: compactor?.name || "" };
      const { data } = await api.put("/settings/company", payload);
      setF({ ...empty, ...data });
      toast.success("Company details saved — new DC prints & auto-forward will use them");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader label="Admin" title="Company Settings" subtitle="These details appear on every printed Delivery Challan · Job-Work compliance" />
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
        <Card title="Consignor Profile (Your Company)">
          <form onSubmit={save} className="space-y-3" data-testid="company-form">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Short Name"><Input required data-testid="company-name" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} /></Field>
              <Field label="Tagline / Long Name"><Input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></Field>
            </div>
            <Field label="Full Address">
              <textarea
                className="w-full h-24 px-3 py-2 bg-white border border-border font-mono text-sm resize-none"
                data-testid="company-address"
                value={f.address}
                onChange={(e) => setF({ ...f, address: e.target.value })}
                placeholder="Door No, Street, Area, City, District, State, PIN"
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="GSTIN"><Input placeholder="15-char GSTIN" data-testid="company-gst" value={f.gst_no} onChange={(e) => setF({ ...f, gst_no: e.target.value.toUpperCase() })} /></Field>
              <Field label="Phone"><Input data-testid="company-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            </div>
            <Field label="Email"><Input type="email" data-testid="company-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="Default Compactor (auto-fill on Dyeing → Compacting)">
              <Select data-testid="company-default-compactor" value={f.default_compactor_id || ""} onChange={(e) => setF({ ...f, default_compactor_id: e.target.value })}>
                <option value="">— none · pick each time —</option>
                {compactors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Btn data-testid="company-submit" disabled={saving}>{saving ? "Saving…" : "Save Company Details"}</Btn>
          </form>
        </Card>

        <Card title="How this appears on your DCs">
          <div className="border border-border p-4 space-y-3 bg-white text-sm">
            <div>
              <div className="font-black text-lg">{f.company_name || "SLKC"}</div>
              <div className="text-xs text-muted-foreground">{f.tagline}</div>
              {f.address && <div className="mt-2 whitespace-pre-line text-xs">{f.address}</div>}
              {f.gst_no && <div className="mt-1 font-mono text-xs"><b>GSTIN:</b> {f.gst_no}</div>}
              {f.phone && <div className="font-mono text-xs"><b>Ph:</b> {f.phone}</div>}
            </div>
            <div className="border-t border-border pt-3">
              <div className="inline-block border-2 border-red-700 text-red-700 font-black text-[10px] tracking-[0.2em] uppercase px-3 py-1">
                For Job Work Only · Not for Sale
              </div>
              <div className="mt-3 text-[10.5px] text-orange-900 bg-orange-50 border border-dashed border-orange-500 p-2">
                This challan is issued <b>strictly for job work</b> under Rule 55 of CGST Rules. Goods are <b>not for sale</b> and must be returned after the specified processing.
              </div>
            </div>
          </div>
        </Card>

        <Card title="Migration · Old Batches → DCs" className="lg:col-span-2">
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Older Knitting / Dyeing / Compacting / Printing entries were saved as single-line <b>batches</b> in the quick-entry form (now retired).
              Convert them to <b>DCs</b> in one click — each old batch becomes a single-line DC labelled <span className="font-mono">MIG-&lt;id&gt;</span>.
              Ledger entries are transferred to the new DC references, so party balances stay identical.
              Safe to re-run · already-migrated batches are skipped.
            </p>
            {preview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label="Migratable" value={preview.total_migratable} testid="mig-total" />
                <StatBox label="Knitting"  value={preview.per_stage.knitting} />
                <StatBox label="Dyeing"    value={preview.per_stage.dyeing} />
                <StatBox label="Compacting"value={preview.per_stage.compacting} />
                <StatBox label="Printing"  value={preview.per_stage.printing} />
                <StatBox label="Auto-from-DC (skipped)"     value={preview.skipped_auto_from_dc} muted />
                <StatBox label="Already migrated (skipped)" value={preview.skipped_already_migrated} muted />
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={runMigration}
                disabled={migrating || (preview && preview.total_migratable === 0)}
                data-testid="mig-run"
                className="h-9 px-4 bg-foreground text-background font-mono text-[11px] tracking-[0.15em] uppercase font-bold disabled:opacity-50"
              >
                {migrating ? "Migrating…" : preview?.total_migratable === 0 ? "Nothing to migrate" : `Migrate ${preview?.total_migratable ?? "?"} batches → DCs`}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatBox({ label, value, muted = false, testid }) {
  return (
    <div className={`border border-border p-3 ${muted ? "bg-secondary/40" : "bg-white"}`} data-testid={testid}>
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className="mt-1 font-display font-black text-2xl tabular tracking-tight">{value ?? 0}</div>
    </div>
  );
}
